import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, SpaceBetween } from '@cloudscape-design/components';
import { useCopilot } from './CopilotContext';
import { CopilotMessageList } from './CopilotMessageList';
import { CopilotMessageInput } from './CopilotMessageInput';
import { ASTRA_ICON_URL } from './brand';
import './Copilot.scss';

interface PanelSize {
  width: number;
  height: number;
}

// Resize bounds (px) for the normal (user-resizable) mode.
const MIN_WIDTH = 320;
const MIN_HEIGHT = 360;
// Gap kept from the viewport side edge when clamping a drag-resize. Width is not
// otherwise capped — the user can drag as wide as the viewport allows.
const SIDE_MARGIN = 24;
// The panel is anchored above the viewport bottom (see `.copilot-panel { bottom: 5% }`);
// this is the fraction of viewport height its bottom edge sits at.
const BOTTOM_ANCHOR_FRACTION = 0.95;
// Fallback top inset if the app chrome can't be measured: TopNavigation (~40px) +
// AppLayout sub-nav/toolbar row (~56px).
const DEFAULT_TOP_INSET = 96;
// Cloudscape's AppLayoutToolbar sub-nav row has no public selector, so we match its
// stable hashed-class substring. If a Cloudscape upgrade renames it, the match returns
// nothing and measurement degrades gracefully to #topnav / DEFAULT_TOP_INSET (no crash).
const APP_LAYOUT_TOOLBAR_SELECTOR = '[class*="awsui_toolbar"]';

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Bottom (viewport px) of the app top chrome — TopNavigation (`#topnav`) + the AppLayout
 * sub-nav/toolbar row. The panel top is clamped below this so its header controls never
 * slide under the sticky toolbar. Falls back to a constant if neither is found (e.g. tests).
 */
function measureTopChromeBottom(): number {
  if (typeof document === 'undefined') return DEFAULT_TOP_INSET;
  let bottom = 0;
  const topNav = document.getElementById('topnav');
  if (topNav) bottom = Math.max(bottom, topNav.getBoundingClientRect().bottom);
  // Sub-nav row (hamburger + breadcrumbs); also accounts for a dismissible banner above it.
  const toolbar = document.querySelector(APP_LAYOUT_TOOLBAR_SELECTOR);
  if (toolbar) bottom = Math.max(bottom, toolbar.getBoundingClientRect().bottom);
  return bottom > 0 ? Math.ceil(bottom) : DEFAULT_TOP_INSET;
}

/** Max panel height that keeps its top at or below the app top chrome. */
function computeMaxHeight(): number {
  if (typeof window === 'undefined') return MIN_HEIGHT;
  const available =
    window.innerHeight * BOTTOM_ANCHOR_FRACTION - measureTopChromeBottom();
  return Math.max(MIN_HEIGHT, Math.floor(available));
}

export function CopilotPanel() {
  const { state, startNewConversation, togglePanel } = useCopilot();
  const panelRef = useRef<HTMLDivElement>(null);
  // Two independent axes so behavior stays consistent across the controls:
  //  - sizeMode: the panel's expanded size ('normal' or 'maximized')
  //  - collapsed: minimized to the header bar only (preserves sizeMode/width on restore)
  const [sizeMode, setSizeMode] = useState<'normal' | 'maximized'>('normal');
  const [collapsed, setCollapsed] = useState(false);
  // Custom size from a user drag-resize (applies to 'normal' sizeMode). null = CSS default.
  const [size, setSize] = useState<PanelSize | null>(null);
  const resizeStart = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  // Ceiling that keeps the panel top below the app top chrome; recomputed on
  // open + window resize. Ref mirrors state so the drag handler reads it live.
  const [maxHeightPx, setMaxHeightPx] = useState<number>(computeMaxHeight);
  const maxHeightRef = useRef<number>(maxHeightPx);

  const onResizePointerMove = useCallback((e: PointerEvent) => {
    const start = resizeStart.current;
    if (!start) return;
    // The panel is anchored bottom-right, so dragging the top-left handle
    // left/up grows it (and right/down shrinks it).
    const width = clamp(
      start.width + (start.x - e.clientX),
      MIN_WIDTH,
      window.innerWidth - SIDE_MARGIN,
    );
    const height = clamp(
      start.height + (start.y - e.clientY),
      MIN_HEIGHT,
      maxHeightRef.current,
    );
    setSize({ width, height });
  }, []);

  const onResizePointerUp = useCallback(() => {
    resizeStart.current = null;
    window.removeEventListener('pointermove', onResizePointerMove);
    window.removeEventListener('pointerup', onResizePointerUp);
  }, [onResizePointerMove]);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;
      e.preventDefault();
      // A manual resize implies a custom, expanded 'normal' size.
      setSizeMode('normal');
      setCollapsed(false);
      resizeStart.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height,
      };
      window.addEventListener('pointermove', onResizePointerMove);
      window.addEventListener('pointerup', onResizePointerUp);
    },
    [onResizePointerMove, onResizePointerUp],
  );

  useEffect(() => {
    const updateMaxHeight = () => {
      const next = computeMaxHeight();
      maxHeightRef.current = next;
      setMaxHeightPx(next);
    };
    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, [state.isOpen]);

  if (!state.isOpen) return null;

  const isMinimized = collapsed;
  const isMaximized = sizeMode === 'maximized';

  // Height ceiling applies in every mode; the custom drag size / maximized width apply
  // only while expanded. Collapsed uses the default width; expanded size is kept in state
  // so restore returns to it.
  const panelStyle: React.CSSProperties = {
    maxHeight: `${maxHeightPx}px`,
    ...(sizeMode === 'normal' && size && !collapsed
      ? { width: `${size.width}px`, height: `${size.height}px` }
      : {}),
  };

  const panelClassName = [
    'copilot-panel',
    isMaximized && !collapsed ? 'copilot-panel--maximized' : '',
    isMinimized ? 'copilot-panel--minimized' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Maximize toggles the expanded size and always un-collapses; minimize toggles the
  // collapsed bar while preserving sizeMode, so restoring returns to the same size.
  const toggleMaximize = () => {
    setSizeMode((m) => (m === 'maximized' ? 'normal' : 'maximized'));
    setCollapsed(false);
  };
  const toggleMinimize = () => setCollapsed((c) => !c);

  return (
    <div
      ref={panelRef}
      className={panelClassName}
      style={panelStyle}
      role="dialog"
      aria-label="Astra Audit Assistant"
    >
      {sizeMode === 'normal' && !collapsed && (
        <div
          className="copilot-resize-handle"
          onPointerDown={onResizePointerDown}
          role="separator"
          aria-label="Resize Astra"
        />
      )}

      {/* awsui-dark-mode scopes Cloudscape's dark tokens to this bar so it matches the
          app's dark TopNavigation; brand text/icons pick up on-dark colors automatically. */}
      <div className="copilot-panel-header awsui-dark-mode">
        <div className="copilot-brand">
          <span className="copilot-brand-icon" aria-hidden="true">
            <img className="copilot-brand-icon-img" src={ASTRA_ICON_URL} alt="" />
          </span>
          <span className="copilot-brand-text">
            <span className="copilot-brand-title">Astra</span>
            <span className="copilot-brand-subtitle">Audit Assistant</span>
          </span>
        </div>
        <SpaceBetween direction="horizontal" size="xxs">
          <Button
            variant="icon"
            iconName="refresh"
            onClick={startNewConversation}
            ariaLabel="New conversation"
          />
          <Button
            variant="icon"
            iconName={isMinimized ? 'angle-up' : 'angle-down'}
            onClick={toggleMinimize}
            ariaLabel={isMinimized ? 'Restore Astra' : 'Minimize Astra'}
          />
          <Button
            variant="icon"
            iconName={isMaximized ? 'shrink' : 'expand'}
            onClick={toggleMaximize}
            ariaLabel={isMaximized ? 'Restore Astra size' : 'Maximize Astra'}
          />
          <Button
            variant="icon"
            iconName="close"
            onClick={togglePanel}
            ariaLabel="Close Astra"
          />
        </SpaceBetween>
      </div>

      {!isMinimized && (
        <>
          <div className="copilot-panel-messages">
            <CopilotMessageList />
          </div>

          <div className="copilot-panel-input">
            <CopilotMessageInput />
          </div>
        </>
      )}
    </div>
  );
}
